import React, { useMemo, useState } from "react";
import {
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Tabs,
  Tab,
} from "@mhome/ui";
import CloseIcon from "@mui/icons-material/Close";
import DetailWidget from "./detail-widget";
import { useMediaQuery } from "@mui/material";
import { useRecoilValue } from "recoil";
import { safeAreaInsetsState, backgroundTypeState } from "../global-state";
import MDialog from "../common/m-dialog";
import { useHistory, useLocation } from "react-router-dom";
import EntityAutomation from "../automation/entity-automation";
import { spacingToPx } from "../lib/utils";
import {
  useComponentBackgroundColor,
  getBackground,
} from "../common/adaptive-theme-provider";

const DetailCardDialog = React.memo(
  ({ card, onValueUpdate, onStateUpdate, onTriggerEvent, onClose }) => {
    const isSmallScreen = useMediaQuery("@media (max-width:959px)");
    const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
    const [activeTab, setActiveTab] = useState(0);
    const getComponentBgColor = useComponentBackgroundColor();
    const backgroundType = useRecoilValue(backgroundTypeState);

    // Check if automation tab should be shown
    const showAutomationTab = useMemo(() => {
      const metaType = card?.meta?.type;
      return (
        metaType === "widget" ||
        metaType === "automation" ||
        metaType === "entity"
      );
    }, [card?.meta?.type]);

    // Memoize widgets and title data to re-render when card.widgets changes
    const { titleWidget, nonTitleWidgets, titleText, subtitleText } =
      useMemo(() => {
        if (!card) {
          return {
            titleWidget: null,
            nonTitleWidgets: [],
            titleText: "",
            subtitleText: "",
          };
        }

        // Find the title widget to display in header
        const titleWidget = card.widgets?.find(
          (widget) =>
            widget.skin === "default.title" ||
            widget.skin === "default.title1" ||
            widget.skin === "default.automationtitle"
        );

        // Filter out only non-automation title widgets from the main content
        // Keep automation title widgets in content as they have special detail view
        const filteredWidgets =
          card.widgets?.filter(
            (widget) =>
              widget.skin !== "default.title" &&
              widget.skin !== "default.title1"
          ) || [];

        // Sort widgets to put automation title widgets first
        const nonTitleWidgets = filteredWidgets.sort((a, b) => {
          const aIsAutomationTitle = a.skin === "default.automationtitle";
          const bIsAutomationTitle = b.skin === "default.automationtitle";

          if (aIsAutomationTitle && !bIsAutomationTitle) return -1;
          if (!aIsAutomationTitle && bIsAutomationTitle) return 1;
          return 0;
        });

        // Get title text from title widget or fallback
        const titleText =
          titleWidget?.skin === "default.automationtitle"
            ? titleWidget?.subtitle
            : titleWidget?.title ||
              card.title ||
              card.name ||
              `Card ${card.id}`;

        // Get subtitle text from title widget
        const subtitleText =
          titleWidget?.skin === "default.automationtitle"
            ? null
            : titleWidget?.subtitle;

        return { titleWidget, nonTitleWidgets, titleText, subtitleText };
      }, [card]);

    // Memoize rendered widgets to re-render when widgets or callbacks change
    const renderedWidgets = useMemo(() => {
      return nonTitleWidgets.map((widget, index) => (
        <div key={widget.id || index} className="w-full h-full">
          <DetailWidget
            widget={widget}
            onValueUpdate={onValueUpdate}
            onStateUpdate={onStateUpdate}
            onTriggerEvent={onTriggerEvent}
          />
        </div>
      ));
    }, [nonTitleWidgets, onValueUpdate, onStateUpdate, onTriggerEvent]);

    if (!card) return null;

    const dialogHash = `detail-card-${card.id}`;
    const history = useHistory();
    const location = useLocation();

    const handleClose = () => {
      const newPath = location.pathname + location.search;
      if (history.length > 1 && location.hash) {
        history.goBack();
      } else {
        history.replace(newPath);
      }
      if (onClose) {
        onClose();
      }
    };

    return (
      <MDialog
        hash={dialogHash}
        fullScreen={isSmallScreen}
        variant={isSmallScreen ? "drawer" : "drawer"}
        onClose={onClose}
        PaperProps={{
          elevation: 0,
          style: isSmallScreen
            ? {
                boxShadow: "none",
                borderRadius: 0,
                width: "100%",
                maxWidth: "100%",
                minWidth: "100%",
                marginTop: 0,
                marginBottom: 0,
                height: "100vh",
                maxHeight: "100vh",
                paddingTop: `${safeAreaInsets.top}px`,
                paddingBottom: `${safeAreaInsets.bottom}px`,
                paddingRight: `${safeAreaInsets.right}px`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }
            : {
                width: "600px",
                maxWidth: "600px",
                minWidth: "600px",
                marginTop: `${safeAreaInsets.top}px !important`,
                marginBottom: `${safeAreaInsets.bottom}px !important`,
                height: `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
                maxHeight: `calc(100vh - ${
                  safeAreaInsets.top + safeAreaInsets.bottom
                }px) !important`,
                paddingTop: 0,
                paddingBottom: 0,
                paddingRight: `${safeAreaInsets.right}px`,
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              },
        }}
      >
        <DialogTitle className="gap-2 flex items-start min-h-0">
          <IconButton
            aria-label="close"
            onClick={handleClose}
            className="shrink-0"
          >
            <CloseIcon />
          </IconButton>
          <div className="flex-1 min-w-0">
            <Typography
              variant="h6"
              component="div"
              className={`${
                subtitleText ? "pt-0" : "pt-1.5"
              } leading-[1.2] mb-0`}
            >
              {titleText}
            </Typography>
            {subtitleText && (
              <Typography
                variant="body2"
                color="text.secondary"
                component="div"
                className="leading-[1.2] text-sm"
              >
                {subtitleText}
              </Typography>
            )}
          </div>
        </DialogTitle>

        <DialogContent
          className={`px-4 ${
            showAutomationTab ? "pb-0" : "pb-6"
          } flex flex-col flex-1 min-h-0 ${
            showAutomationTab ? "overflow-hidden" : "overflow-auto"
          }`}
        >
          {showAutomationTab ? (
            <>
              {/* Scrollable Content Area */}
              <div
                className="flex-1 overflow-y-auto overflow-x-hidden pb-4 min-h-0"
                style={{
                  // Allow scrolling but don't interfere with slider interactions
                  touchAction: "pan-y",
                }}
              >
                {activeTab === 0 &&
                  (() => {
                    // Use current theme background color to match the main app background
                    const overviewBg =
                      getBackground(backgroundType) ||
                      `hsl(var(--background))` ||
                      "#FFFFFF";

                    // Check if overviewBg is a gradient
                    const isGradient =
                      typeof overviewBg === "string" &&
                      (overviewBg.startsWith("linear-gradient") ||
                        overviewBg.startsWith("radial-gradient") ||
                        overviewBg.startsWith("conic-gradient"));

                    return (
                      <div
                        className="pt-3 flex flex-col gap-3 w-full"
                        style={{
                          border: `1px solid hsl(var(--border))`,
                          borderRadius: "16px",
                          padding: "16px",
                          // Use background for gradients, backgroundColor for solid colors
                          ...(isGradient
                            ? { background: overviewBg }
                            : { backgroundColor: overviewBg }),
                        }}
                      >
                        {renderedWidgets}
                      </div>
                    );
                  })()}

                {activeTab === 1 && card.id && (
                  <div
                    className="pt-3"
                    style={{
                      border: `1px solid ${"hsl(var(--border))"}`,
                      borderRadius: "16px",
                      padding: "16px",
                      backgroundColor: getComponentBgColor("hsl(var(--card))"),
                    }}
                  >
                    <EntityAutomation
                      automationFetch={{
                        entityType: card.meta?.entityType,
                        integration: card.meta?.integration,
                        entityId: card.meta?.entityId,
                        filter: {},
                      }}
                      inspireFetch={
                        card.cardEntityAutomation?.inspireSuggests || []
                      }
                    />
                  </div>
                )}
              </div>

              {/* Fixed Bottom Tab Navigation */}
              <div
                className="relative pt-2 bg-transparent"
                style={{
                  borderColor: "hsl(var(--border))",
                  paddingBottom: isSmallScreen
                    ? `${safeAreaInsets.bottom}px`
                    : spacingToPx(1),
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  variant="standard"
                  className="min-h-[48px]"
                >
                  <Tab label="Overview" />
                  <Tab label="Automations" />
                </Tabs>
              </div>
            </>
          ) : (
            /* No tabs - just show widgets */
            <div
              className="pt-3 flex flex-col gap-3 w-full"
              style={{
                border: `1px solid ${"hsl(var(--border))"}`,
                borderRadius: "16px",
                padding: "16px",
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
              }}
            >
              {renderedWidgets}
            </div>
          )}
        </DialogContent>
      </MDialog>
    );
  },
  (prevProps, nextProps) => {
    return prevProps.card === nextProps.card;
  }
);

DetailCardDialog.displayName = "DetailCardDialog";

export default DetailCardDialog;
